import type { ImportLogDto, ImportPreviewDto, ImportType, Paginated } from '@bnn/shared';
import { useRef, useState } from 'react';
import { Alert, Badge, Button, Card, PageHeader, Pagination, Table } from '../components/ui';
import { api } from '../lib/api';
import { errMsg, useApi } from '../lib/useApi';

const TYPES: { type: ImportType; step: string; title: string; desc: string }[] = [
  { type: 'uoms', step: '1', title: 'UoM Group', desc: 'ไฟล์ "BNN UOM MASTER" จาก SAP (Base/Alternative Quantity ของแต่ละกลุ่ม) — Import ก่อนไฟล์สินค้า' },
  { type: 'items', step: '2', title: 'รายการสินค้า', desc: 'ไฟล์ "List of Items" จาก SAP — ใช้เฉพาะรหัส ชื่อ กลุ่ม หน่วย และสถานะ (ไม่นำเข้ายอดคงเหลือหรือต้นทุน)' },
  { type: 'barcodes', step: '3', title: 'Barcode', desc: 'ไม่บังคับ — ใช้ Template ของระบบ (คอลัมน์ Barcode เป็น Text) หรือบันทึก Barcode ระหว่างนับได้ใน M2' },
];
const LABEL: Record<ImportType, string> = { uoms: 'UoM Group', items: 'สินค้า', barcodes: 'Barcode' };
const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  insert: { label: 'เพิ่มใหม่', tone: 'ok' },
  update: { label: 'อัปเดต', tone: 'warn' },
  unchanged: { label: 'ไม่เปลี่ยน', tone: 'neutral' },
  error: { label: 'Error', tone: 'bad' },
};

export default function ImportsPage() {
  const [type, setType] = useState<ImportType>('uoms');
  const [logPage, setLogPage] = useState(1);
  const logs = useApi<Paginated<ImportLogDto>>(`/imports?page=${logPage}&page_size=10`);
  const current = TYPES.find((t) => t.type === type)!;

  return (
    <>
      <PageHeader title="นำเข้า Master" subtitle="อัปโหลด → ตรวจ Preview → ยืนยัน · Import ซ้ำได้ ระบบไม่ลบข้อมูลหรือประวัติเดิม" />
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        {TYPES.map((t) => (
          <button
            key={t.type}
            onClick={() => setType(t.type)}
            className={`rounded-xl border p-3 text-left transition ${t.type === type ? 'border-brand bg-brand/5' : 'border-line bg-white hover:border-brand/50'}`}
          >
            <div className="text-xs text-muted">ขั้นที่ {t.step}</div>
            <div className="font-semibold">{t.title}</div>
          </button>
        ))}
      </div>
      <ImportPanel key={type} type={type} desc={current.desc} title={current.title} onCommitted={logs.reload} />
      <Card title="ประวัติการนำเข้า" className="mt-4">
        {logs.data && (
          <>
            <Table head={['เวลา', 'ประเภท', 'ไฟล์', 'เพิ่ม', 'อัปเดต', 'ไม่เปลี่ยน', 'Error', 'โดย']} minWidth={760}>
              {logs.data.data.map((l) => (
                <tr key={l.id}>
                  <td className="whitespace-nowrap">{new Date(l.created_at).toLocaleString('th-TH')}</td>
                  <td>{LABEL[l.import_type]}</td>
                  <td className="max-w-56 truncate" title={l.file_name}>
                    {l.file_name}
                  </td>
                  <td>{l.rows_inserted.toLocaleString()}</td>
                  <td>{l.rows_updated.toLocaleString()}</td>
                  <td>{l.rows_unchanged.toLocaleString()}</td>
                  <td className={l.rows_error ? 'text-bad' : ''}>{l.rows_error.toLocaleString()}</td>
                  <td>{l.user.display_name}</td>
                </tr>
              ))}
            </Table>
            <Pagination page={logs.data.page} pageSize={logs.data.page_size} total={logs.data.total} onPage={setLogPage} />
          </>
        )}
      </Card>
    </>
  );
}

function ImportPanel({ type, title, desc, onCommitted }: { type: ImportType; title: string; desc: string; onCommitted: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreviewDto | null>(null);
  const [mode, setMode] = useState<'ALL_OR_NOTHING' | 'VALID_ONLY'>('ALL_OR_NOTHING');
  const [busy, setBusy] = useState<'upload' | 'commit' | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState<string>('');

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setDone('');
    setPreview(null);
    setBusy('upload');
    try {
      setPreview(await api.upload<ImportPreviewDto>(`/imports/${type}/preview`, file));
      setMode('ALL_OR_NOTHING');
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    if (!preview) return;
    setError('');
    setBusy('commit');
    try {
      const r = await api.post<{ summary: { insert: number; update: number; unchanged: number; error: number } }>(`/imports/${type}/commit`, {
        preview_id: preview.preview_id,
        mode,
      });
      setDone(
        `นำเข้าเรียบร้อย: เพิ่ม ${r.summary.insert.toLocaleString()} · อัปเดต ${r.summary.update.toLocaleString()} · ไม่เปลี่ยน ${r.summary.unchanged.toLocaleString()}` +
          (r.summary.error ? ` · ข้าม Error ${r.summary.error.toLocaleString()}` : ''),
      );
      setPreview(null);
      onCommitted();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  const s = preview?.summary;
  const nothingToDo = s && s.insert + s.update === 0;

  return (
    <Card
      title={`นำเข้า ${title}`}
      actions={
        <Button size="sm" onClick={() => void api.download(`/imports/${type}/template`, `template_${type}.xlsx`).catch((e) => setError(errMsg(e)))}>
          ⬇ Template
        </Button>
      }
    >
      <p className="mb-3 text-sm text-muted">{desc}</p>
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line px-4 py-6 text-center hover:border-brand">
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} disabled={!!busy} />
        {busy === 'upload' ? 'กำลังอ่านไฟล์…' : 'เลือกไฟล์ .xlsx หรือ .csv (ไม่เกิน 10 MB)'}
      </label>

      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}
      {done && (
        <div className="mt-3">
          <Alert tone="ok">{done}</Alert>
        </div>
      )}

      {preview && s && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <b className="mr-1">{preview.file_name}</b>
            <Badge>{s.total.toLocaleString()} รายการ</Badge>
            <Badge tone="ok">เพิ่มใหม่ {s.insert.toLocaleString()}</Badge>
            <Badge tone="warn">อัปเดต {s.update.toLocaleString()}</Badge>
            <Badge>ไม่เปลี่ยน {s.unchanged.toLocaleString()}</Badge>
            <Badge tone={s.error ? 'bad' : 'neutral'}>Error {s.error.toLocaleString()}</Badge>
          </div>
          {preview.warnings.map((w) => (
            <Alert key={w} tone="warn">
              {w}
            </Alert>
          ))}
          {preview.rows.length > 0 && (
            <div className="max-h-96 overflow-auto rounded-lg border border-line px-4">
              <Table head={['แถว', 'รหัส', 'ผล', 'รายละเอียด']} minWidth={600}>
                {preview.rows.map((r) => (
                  <tr key={`${r.row}-${r.key}`}>
                    <td>{r.row}</td>
                    <td className="font-mono text-xs break-all">{r.key}</td>
                    <td>
                      <Badge tone={STATUS[r.status]!.tone}>{STATUS[r.status]!.label}</Badge>
                    </td>
                    <td className="text-xs">
                      {r.errors.map((e) => (
                        <div key={e} className="text-bad">
                          {e}
                        </div>
                      ))}
                      {r.warnings.map((w) => (
                        <div key={w} className="text-warn">
                          {w}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
          {preview.rows.length >= 500 && <p className="text-xs text-muted">แสดง 500 แถวแรก (Error ก่อน)</p>}
          {s.error > 0 && (
            <fieldset className="space-y-1 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" checked={mode === 'ALL_OR_NOTHING'} onChange={() => setMode('ALL_OR_NOTHING')} /> ยกเลิกทั้งไฟล์ถ้ามี Error (แนะนำ — แก้ไฟล์แล้วอัปโหลดใหม่)
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" checked={mode === 'VALID_ONLY'} onChange={() => setMode('VALID_ONLY')} /> นำเข้าเฉพาะแถวที่ผ่าน ข้ามแถวที่ Error
              </label>
            </fieldset>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              loading={busy === 'commit'}
              disabled={!!nothingToDo || (s.error > 0 && mode === 'ALL_OR_NOTHING')}
              onClick={() => void commit()}
            >
              ยืนยันนำเข้า
            </Button>
            <Button onClick={() => setPreview(null)}>ยกเลิก</Button>
            {nothingToDo && <span className="self-center text-sm text-muted">ไม่มีอะไรเปลี่ยน — ข้อมูลตรงกับระบบแล้ว</span>}
          </div>
          <p className="text-xs text-muted">Preview ใช้ได้ถึง {new Date(preview.expires_at).toLocaleTimeString('th-TH')}</p>
        </div>
      )}
    </Card>
  );
}
